import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { BID_STATUS, CANCEL_IN_WORK_ROLES, FINE_STATUS } from '@/shared/constants/bids';
import { formatCost, formatUnixDateTime } from '@/shared/lib/datetime';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { fetchAllBidOffers, fetchBid, type BidDetail, type BidWorkOrderFile } from './detailApi';
import { AtWorkAction, AttachOfferBlock, CancelAction } from './detail/BidActions';
import { CloseHistory, FinancialHistory } from './detail/BidHistoryTables';
import { OffersTable, ViewedWithoutOfferTable, WhoSawBidTable } from './detail/BidServicesTables';
import { CreateReviewForm } from './detail/CreateReviewForm';
import { DistributeBidBlock } from './detail/DistributeBidBlock';
import { FileGallery } from './detail/FileGallery';
import { formatWorkTypes } from './detail/format';
import { PhoneCell } from './detail/PhoneCell';
import { WorkOrderSection } from './detail/WorkOrderSection';
import { bidKey } from './detail/useBidAction';

/** Legacy `finishedId` — «Ремонт окончен», distinct from DONE/closed (5). */
const BID_STATUS_FINISHED = 3;

const STATUS_LABEL: Record<number, { className: string; text: string }> = {
  [BID_STATUS.NEW]: { className: 'label-info', text: 'Новая' },
  [BID_STATUS.PREMODERATION]: { className: 'label-warning', text: 'Премодерация' },
  1: { className: 'label-primary', text: 'В работе' },
  [BID_STATUS.IN_WORK]: { className: 'label-primary', text: 'В работе' },
  [BID_STATUS.DONE]: { className: 'label-default', text: 'Ремонт завершен' },
  [BID_STATUS.CANCELLED]: { className: 'label-danger', text: 'Отмененная' },
};

const COMMUNICATION_LABEL: Record<number, { className: string; text: string }> = {
  1: { className: 'label-default', text: 'Сообщение' },
  2: { className: 'label-info', text: 'Телефон' },
};

const AUTOPARK_ROLE = 'autopark';
const FEW_ANSWERS_MAX = 1;

const Badge = ({ label }: { label: { className: string; text: string } }) => (
  <span className={`label ${label.className}`}>{label.text}</span>
);

const InfoRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <li className="list-group-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <b>{label}</b>
    <span style={{ maxWidth: '60%', wordBreak: 'break-word', textAlign: 'right' }}>{children}</span>
  </li>
);

const answersBadgeClass = (count: number) => {
  if (count === 0) return 'label-danger';
  return count <= FEW_ANSWERS_MAX ? 'label-warning' : 'label-default';
};

const ServiceBox = ({ bid }: { bid: BidDetail }) => {
  const { fine } = bid;
  const fineStatus = fine?.status_id?.status;
  const serviceLink = `/users/user/${bid.service_id}`;

  return (
    <div className="box box-primary">
      <div className="box-body box-profile">
        <ul className="list-group list-group-unbordered">
          <InfoRow label="ID СТО">
            <Link to={serviceLink}>{bid.service_id}</Link>
          </InfoRow>
          <InfoRow label="Название СТО">
            <Link to={serviceLink}>{bid.service_name}</Link>
          </InfoRow>
          {fine && (
            <>
              <InfoRow label="Текущий баланс СТО">
                {(fine.service_balance ?? []).map(({ id, balance }) => (
                  <div key={id}>{formatCost(balance)} ₽</div>
                ))}
              </InfoRow>
              <InfoRow label="Штраф по заявке">{formatCost(fine.fine_cost?.total_sum)} ₽</InfoRow>
              <InfoRow label="Дата и время жалобы">{formatUnixDateTime(fine.request_time)}</InfoRow>
              <InfoRow label="Статус жалобы">{fineStatus === undefined ? '' : FINE_STATUS[fineStatus]}</InfoRow>
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

const BidInfoBox = ({ bid, canCancelInWork }: { bid: BidDetail; canCancelInWork: boolean }) => {
  const { auto, customer, fine, applied_discount: discount } = bid;
  const customerId = customer?.id;

  return (
    <div className="box box-primary">
      <div className="box-body box-profile">
        <ul className="list-group list-group-unbordered">
          <InfoRow label="Марка">{auto ? auto.mark?.name || 'нет марки' : 'нет списка марок'}</InfoRow>
          <InfoRow label="Модель">{auto ? auto.model?.name || 'нет модели' : 'нет списка моделей'}</InfoRow>
          <InfoRow label="Поколение">{auto?.generation?.generation_name ?? ''}</InfoRow>
          <InfoRow label="Модификация">{auto?.modification?.name || 'Не указано'}</InfoRow>
          {bid.status_id === BID_STATUS.NEW && (
            <InfoRow label="Сменить статус заявки">
              <AtWorkAction bidId={bid.id} />
            </InfoRow>
          )}
          <InfoRow label="Название автосервиса">
            {customer ? customer.name || 'нет названия автосервиса' : 'нет списка автовладельца'}
          </InfoRow>
          <InfoRow label="ФИО">{customer?.contact_person || 'Не указано'}</InfoRow>
          <InfoRow label="Телефон владельца">
            <PhoneCell userId={customerId} phone={customer?.phone} />
          </InfoRow>
          <InfoRow label="Дата создания заявки">{formatUnixDateTime(bid.create_time)}</InfoRow>
          <InfoRow label="Предпочтительный вариант связи">
            <Badge label={COMMUNICATION_LABEL[Number(bid.type_connection)] ?? { className: 'label-danger', text: 'Не выбран' }} />
          </InfoRow>
          <InfoRow label="Тип работ">{bid.work_type ? formatWorkTypes(bid.work_type) : 'нет списка работ'}</InfoRow>
          <InfoRow label="Период сдачи авто">
            {formatUnixDateTime(bid.period_start)} - {formatUnixDateTime(bid.period_end)}
          </InfoRow>
          <InfoRow label="Статус">
            <Badge
              label={
                STATUS_LABEL[Number(bid.status_id)] ?? {
                  className: 'label-default',
                  text: `Статус неопределен: ${bid.status_id}`,
                }
              }
            />
          </InfoRow>
          <InfoRow label="Штраф">{fine ? 'начислен' : 'нет'}</InfoRow>
          <InfoRow label="Запрос на отмену штрафа">{fine?.is_request_fine ? 'есть' : 'нет'}</InfoRow>
          {bid.status_id === BID_STATUS.IN_WORK && canCancelInWork && (
            <InfoRow label="Отмена заявки">
              <CancelAction bidId={bid.id} />
            </InfoRow>
          )}
          {customerId && (
            <InfoRow label="Профиль заказчика">
              <Link to={`/users/user/${customerId}`} className="btn btn-primary btn-sm">
                Открыть
              </Link>
            </InfoRow>
          )}
          <InfoRow label="Источник">{bid.utm_source ?? ''}</InfoRow>
          <InfoRow label="Лояльность">{bid.attached_promo_code?.code ?? ''}</InfoRow>
          <InfoRow label="Партнер">{bid.partner_name}</InfoRow>
          <InfoRow label="Устройство">{bid.source}</InfoRow>
          <InfoRow label="Скидка">
            {discount ? `${discount.applied_discount_percent}% ${discount.discount_name ?? ''}` : 'Нет скидки'}
          </InfoRow>
        </ul>
      </div>
    </div>
  );
};

const Box = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="box box-primary">
    <div className="box-header with-border">
      <h3 className="box-title">{title}</h3>
    </div>
    <div className="box-body">{children}</div>
  </div>
);

const BidTextBoxes = ({
  bid,
  resultPhotos,
}: {
  bid: BidDetail;
  resultPhotos: BidWorkOrderFile[];
}) => {
  const answers = Number(bid.answers_count);
  const images = bid.images ?? [];
  const statusId = Number(bid.status_id);
  const showReview = statusId === BID_STATUS_FINISHED || statusId === BID_STATUS.CANCELLED;
  const showResultPhotos =
    (statusId === BID_STATUS_FINISHED || statusId === BID_STATUS.DONE) && resultPhotos.length > 0;
  const showWorkOrder = Boolean(bid.work_order) || statusId === BID_STATUS.IN_WORK;

  return (
    <>
      <Box title="Текст заявки">
        <p>{bid.description}</p>
        {answers > 0 ? (
          <div>
            <b>Количество ответов:</b> <span className={`label ${answersBadgeClass(answers)}`}>{answers}</span>
          </div>
        ) : (
          <div>Нет ответов</div>
        )}
      </Box>
      {images.length > 0 && (
        <Box title="Фото">
          {images.map((image, index) => (
            <a key={`${image.url}-${index}`} href={image.url} target="_blank" rel="noopener noreferrer">
              <img
                src={image.thumb_140_140 || image.thumb_120_120 || image.url}
                alt=""
                style={{ height: 120, margin: '0 8px 8px 0' }}
              />
            </a>
          ))}
        </Box>
      )}
      {bid.comment && (
        <Box title="Комментарий">
          <p>{bid.comment}</p>
        </Box>
      )}
      {showReview && bid.customer?.id != null && (
        <Box title="Добавить отзыв">
          <CreateReviewForm
            bidId={bid.id}
            customerId={bid.customer.id}
            serviceId={bid.service_id}
            statusId={statusId}
          />
        </Box>
      )}
      {showResultPhotos && (
        <Box title="Фото от автосервиса (добавленные до заказ-наряда)">
          <FileGallery files={resultPhotos} thumb="thumb_120_120" />
        </Box>
      )}
      {showWorkOrder && <WorkOrderSection bid={bid} />}
    </>
  );
};

export const BidDetailPage = () => {
  const bidId = Number(useParams().id);
  const roles = useAuthStore((s) => s.profile.roles);
  const { data: bid, isLoading, error } = useQuery({
    queryKey: bidKey(bidId, 'view'),
    queryFn: () => fetchBid(bidId),
  });
  const offersQuery = useQuery({
    queryKey: bidKey(bidId, 'offers-all'),
    queryFn: () => fetchAllBidOffers(bidId),
    enabled: Number.isFinite(bidId) && bidId > 0,
  });

  const canCancelInWork = CANCEL_IN_WORK_ROLES.some((role) => role in roles);
  const isAutopark = Boolean(bid?.customer?.roles?.some((role) => role.value === AUTOPARK_ROLE));
  const resultPhotos =
    offersQuery.data?.find((row) => Number(row.service?.id) === Number(bid?.service_id))
      ?.result_doc_photos ?? [];
  const sectionProps = {
    bidId,
    serviceId: bid?.service_id,
    createTime: bid?.create_time,
    finalDate: bid?.final_date,
  };

  return (
    <>
      <section className="content-header">
        <h1>Заявка #{bidId}</h1>
      </section>
      <section className="content">
        <QueryStatus isLoading={isLoading} error={error} errorText="Не удалось загрузить заявку" />
        {bid && (
          <>
            <div className="row">
              <div className="col-md-3">
                {bid.service_id ? <ServiceBox bid={bid} /> : null}
                <BidInfoBox bid={bid} canCancelInWork={canCancelInWork} />
              </div>
              <div className="col-md-9">
                <BidTextBoxes bid={bid} resultPhotos={resultPhotos} />
              </div>
            </div>
            <FinancialHistory operations={bid.financial_operation} />
            <CloseHistory bidId={bidId} />
            {bid.status_id === BID_STATUS.PREMODERATION && !bid.service_id && <DistributeBidBlock bidId={bidId} />}
            <OffersTable {...sectionProps} />
            <ViewedWithoutOfferTable {...sectionProps} />
            <WhoSawBidTable {...sectionProps} />
            {isAutopark && <AttachOfferBlock bidId={bidId} />}
          </>
        )}
      </section>
    </>
  );
};
